<script>
/**
 * @component ContractDetail
 * @desc Detalle de un contrato médico. Permite editar datos generales
 *       y gestionar las prestaciones con sus precios.
 *       Ruta /contracts/new para crear, /contracts/:id para editar.
 */
import medicalContractService from "@/services/medical-contract";
import practiceCatalogService from "@/services/practice-catalog";
import doctorService from "@/services/doctor";
import { mapGetters } from "vuex";
import debounce from "debounce";

const DEBOUNCE_MS = 400;

export default {
  name: "ContractDetail",
  computed: {
    ...mapGetters(["isAdmin"]),
    /** @returns {boolean} Si es creación nueva */
    isNew() {
      return this.$route.name === "contract-new";
    },
    /** @returns {string} ID del contrato */
    contractId() {
      return this.$route.params.id;
    },
  },
  data() {
    return {
      activeTab: "general",
      loading: false,
      saving: false,
      contract: null,
      form: {
        provider: "",
        contractNumber: "",
        status: "draft",
        startsAt: "",
        endsAt: "",
        paymentModality: "per-practice",
        sameValueForAllPlans: true,
        notes: "",
      },
      formErrors: {},
      providerOptions: [],
      providerSearchLoading: false,
      // Prestaciones
      items: [],
      itemsTotal: 0,
      itemsPage: 1,
      itemsPageSize: 20,
      itemsLoading: false,
      itemsSearch: "",
      // Dialog agregar prestación
      itemDialogVisible: false,
      itemForm: {
        practice: "",
        practiceLabel: "",
        isEnabled: true,
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      },
      itemFormErrors: {},
      practiceOptions: [],
      practiceSearchLoading: false,
      itemSaving: false,
      // Dialog agregar precio
      priceDialogVisible: false,
      selectedItem: null,
      priceForm: {
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      },
      priceSaving: false,
    };
  },
  created() {
    if (!this.isNew) this.fetchContract();
  },
  methods: {
    /**
     * @method fetchContract
     * @desc Carga el contrato y sus datos generales
     */
    async fetchContract() {
      this.loading = true;
      try {
        this.contract = await medicalContractService.getById(this.contractId);
        this.form = {
          provider: this.contract.provider?._id || this.contract.provider,
          contractNumber: this.contract.contractNumber || "",
          status: this.contract.status,
          startsAt: this.contract.startsAt
            ? this.contract.startsAt.split("T")[0]
            : "",
          endsAt: this.contract.endsAt
            ? this.contract.endsAt.split("T")[0]
            : "",
          paymentModality: this.contract.paymentModality,
          sameValueForAllPlans: this.contract.sameValueForAllPlans,
          notes: this.contract.notes || "",
        };
        if (this.contract.provider) {
          this.providerOptions = [
            {
              value: this.contract.provider._id,
              label: `${this.contract.provider.firstName} ${this.contract.provider.lastName}`,
            },
          ];
        }
      } catch {
        this.$message.error("Error al cargar el contrato");
      } finally {
        this.loading = false;
      }
    },

    /**
     * @method fetchItems
     * @desc Carga las prestaciones del contrato con paginación y búsqueda
     */
    async fetchItems() {
      this.itemsLoading = true;
      try {
        const params = {
          offset: (this.itemsPage - 1) * this.itemsPageSize,
          limit: this.itemsPageSize,
        };
        if (this.itemsSearch) params.searchInput = this.itemsSearch;
        const result = await medicalContractService.getItems(
          this.contractId,
          params
        );
        this.items = result.docs || [];
        this.itemsTotal = result.total || 0;
      } catch {
        this.$message.error("Error al cargar prestaciones");
      } finally {
        this.itemsLoading = false;
      }
    },

    debounceItemsSearch: debounce(function () {
      this.itemsPage = 1;
      this.fetchItems();
    }, DEBOUNCE_MS),

    /**
     * @method onTabChange
     * @desc Al cambiar a tab prestaciones, carga los items
     * @param {Object} tab - Tab seleccionado
     */
    onTabChange(tab) {
      if (tab === "items" && !this.isNew) this.fetchItems();
    },

    /**
     * @method onTabClick
     * @desc Handler para el evento tab-click de Element UI
     * @param {Object} tab - Instancia del tab clickeado
     */
    onTabClick(tab) {
      this.onTabChange(tab.name);
    },

    /**
     * @method saveContract
     * @desc Crea o actualiza el contrato
     */
    async saveContract() {
      this.formErrors = {};
      if (!this.form.provider) this.formErrors.provider = "Requerido";
      if (!this.form.startsAt) this.formErrors.startsAt = "Requerido";
      if (Object.keys(this.formErrors).length) return;

      this.saving = true;
      try {
        if (this.isNew) {
          const contract = await medicalContractService.create(this.form);
          this.$message.success("Contrato creado");
          this.$router.replace({
            name: "contract-detail",
            params: { id: contract._id },
          });
        } else {
          await medicalContractService.update(this.contractId, this.form);
          this.$message.success("Contrato actualizado");
        }
      } catch {
        this.$message.error("Error al guardar el contrato");
      } finally {
        this.saving = false;
      }
    },

    /**
     * @method searchProviders
     * @desc Búsqueda remota de prestadores para el selector
     * @param {string} query - Texto de búsqueda
     */
    async searchProviders(query) {
      if (!query) return;
      this.providerSearchLoading = true;
      try {
        const doctors = await doctorService.getDoctors({ search: query });
        this.providerOptions = (
          Array.isArray(doctors) ? doctors : doctors.docs || []
        ).map((d) => ({
          value: d._id,
          label: `${d.firstName} ${d.lastName}`,
        }));
      } finally {
        this.providerSearchLoading = false;
      }
    },

    /**
     * @method searchPractices
     * @desc Búsqueda remota de prestaciones para el selector del dialog
     * @param {string} query - Texto de búsqueda
     */
    async searchPractices(query) {
      if (!query) return;
      this.practiceSearchLoading = true;
      try {
        const result = await practiceCatalogService.query({
          searchInput: query,
          limit: 20,
        });
        this.practiceOptions = (result.docs || []).map((p) => ({
          value: p._id,
          label: `${p.code} - ${p.name}`,
        }));
      } finally {
        this.practiceSearchLoading = false;
      }
    },

    /**
     * @method openItemDialog
     * @desc Abre el dialog para agregar una prestación
     */
    openItemDialog() {
      this.itemForm = {
        practice: "",
        practiceLabel: "",
        isEnabled: true,
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      };
      this.practiceOptions = [];
      this.itemFormErrors = {};
      this.itemDialogVisible = true;
    },

    /**
     * @method saveItem
     * @desc Guarda la prestación en el contrato
     */
    async saveItem() {
      this.itemFormErrors = {};
      if (!this.itemForm.practice) {
        this.itemFormErrors.practice = "Requerido";
        return;
      }
      this.itemSaving = true;
      try {
        await medicalContractService.upsertItem(this.contractId, {
          practice: this.itemForm.practice,
          isEnabled: this.itemForm.isEnabled,
          price: {
            amount: this.itemForm.amount,
            effectiveFrom: this.itemForm.effectiveFrom,
            hasCopago: this.itemForm.hasCopago,
            copagoAmount: this.itemForm.copagoAmount,
            hasCoseguro: this.itemForm.hasCoseguro,
            coseguroPercent: this.itemForm.coseguroPercent,
          },
        });
        this.$message.success("Prestación agregada");
        this.itemDialogVisible = false;
        this.fetchItems();
      } catch {
        this.$message.error("Error al agregar la prestación");
      } finally {
        this.itemSaving = false;
      }
    },

    /**
     * @method openPriceDialog
     * @desc Abre el dialog para agregar un nuevo precio a una prestación
     * @param {Object} item - Item del contrato
     */
    openPriceDialog(item) {
      this.selectedItem = item;
      this.priceForm = {
        amount: 0,
        effectiveFrom: new Date().toISOString().split("T")[0],
        hasCopago: false,
        copagoAmount: 0,
        hasCoseguro: false,
        coseguroPercent: 0,
      };
      this.priceDialogVisible = true;
    },

    /**
     * @method savePrice
     * @desc Agrega un precio al historial de la prestación
     */
    async savePrice() {
      this.priceSaving = true;
      try {
        await medicalContractService.addPrice(
          this.contractId,
          this.selectedItem._id,
          this.priceForm
        );
        this.$message.success("Precio actualizado");
        this.priceDialogVisible = false;
        this.fetchItems();
      } catch {
        this.$message.error("Error al guardar el precio");
      } finally {
        this.priceSaving = false;
      }
    },

    /**
     * @method toggleItemEnabled
     * @desc Activa/desactiva una prestación en el contrato
     * @param {Object} item
     */
    async toggleItemEnabled(item) {
      try {
        await medicalContractService.updateItem(this.contractId, item._id, {
          isEnabled: !item.isEnabled,
        });
        item.isEnabled = !item.isEnabled;
      } catch {
        this.$message.error("Error al actualizar la prestación");
      }
    },

    /**
     * @method removeItem
     * @desc Elimina una prestación del contrato
     * @param {Object} item
     */
    async removeItem(item) {
      try {
        await this.$confirm(
          "¿Eliminar esta prestación del contrato?",
          "Confirmar",
          {
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await medicalContractService.removeItem(this.contractId, item._id);
        this.$message.success("Prestación eliminada");
        this.fetchItems();
      } catch {
        /* cancelado */
      }
    },

    /**
     * @method getCurrentPrice
     * @desc Obtiene el precio vigente de un item
     * @param {Object} item
     * @returns {Object|null}
     */
    getCurrentPrice(item) {
      if (!item.prices || !item.prices.length) return null;
      const now = new Date();
      return (
        item.prices
          .filter((p) => new Date(p.effectiveFrom) <= now)
          .sort(
            (a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom)
          )[0] || null
      );
    },

    /** @method formatDate */
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("es-AR") : "-";
    },

    /** @method formatCurrency */
    formatCurrency(n) {
      return n != null ? `$${Number(n).toLocaleString("es-AR")}` : "-";
    },

    /** @method goBack */
    goBack() {
      this.$router.push({ name: "contracts" });
    },
  },
};
</script>

<template lang="pug">
div(v-loading="loading")
  .page-header
    .page-header__left
      el-button(type="text" icon="el-icon-arrow-left" @click="goBack") Contratos
      h1.page-title {{ isNew ? 'Nuevo contrato' : (contract ? 'Contrato ' + (contract.contractNumber || '') : '') }}
    el-button(type="primary" :loading="saving" @click="saveContract") {{ isNew ? 'Crear contrato' : 'Guardar cambios' }}

  el-tabs(v-model="activeTab" @tab-click="onTabClick")

    //- Tab datos generales
    el-tab-pane(label="Datos generales" name="general")
      el-form(label-position="top" size="small")
        .form-card
          el-row(:gutter="16")
            el-col(:span="12")
              el-form-item(label="Prestador" required :error="formErrors.provider")
                el-select(
                  v-model="form.provider"
                  filterable
                  remote
                  reserve-keyword
                  placeholder="Buscar prestador..."
                  :remote-method="searchProviders"
                  :loading="providerSearchLoading"
                  style="width: 100%"
                )
                  el-option(v-for="o in providerOptions" :key="o.value" :label="o.label" :value="o.value")
            el-col(:span="12")
              el-form-item(label="N° de contrato")
                el-input(v-model="form.contractNumber" placeholder="Ej: CTR-2026-001")
          el-row(:gutter="16")
            el-col(:span="8")
              el-form-item(label="Estado")
                el-select(v-model="form.status" style="width: 100%")
                  el-option(label="Borrador" value="draft")
                  el-option(label="Activo" value="active")
                  el-option(label="Vencido" value="expired")
                  el-option(label="Rescindido" value="terminated")
            el-col(:span="8")
              el-form-item(label="Fecha de inicio" required :error="formErrors.startsAt")
                el-input(v-model="form.startsAt" type="date" style="width: 100%")
            el-col(:span="8")
              el-form-item(label="Fecha de vencimiento")
                el-input(v-model="form.endsAt" type="date" style="width: 100%")
          el-row(:gutter="16")
            el-col(:span="12")
              el-form-item(label="Modalidad de pago")
                el-select(v-model="form.paymentModality" style="width: 100%")
                  el-option(label="Por prestación" value="per-practice")
                  el-option(label="Mensual" value="monthly")
                  el-option(label="Mixto" value="mixed")
            el-col(:span="12")
              el-form-item(label=" ")
                el-checkbox(v-model="form.sameValueForAllPlans") Mismo valor para todos los planes
          el-form-item(label="Notas")
            el-input(v-model="form.notes" type="textarea" :rows="3")

    //- Tab prestaciones
    el-tab-pane(label="Prestaciones" name="items" :disabled="isNew")
      .items-toolbar
        el-input(
          v-model="itemsSearch"
          placeholder="Buscar prestación..."
          prefix-icon="el-icon-search"
          clearable
          size="small"
          style="width: 300px"
          @input="debounceItemsSearch"
          @clear="debounceItemsSearch"
        )
        el-button(type="primary" size="small" icon="el-icon-plus" @click="openItemDialog") Agregar prestación

      el-table(
        :data="items"
        v-loading="itemsLoading"
        border
        size="small"
        style="margin-top: 12px"
      )
        el-table-column(label="Código" width="100")
          template(slot-scope="{ row }") {{ row.practice ? row.practice.code : '-' }}
        el-table-column(label="Prestación")
          template(slot-scope="{ row }") {{ row.practice ? row.practice.name : '-' }}
        el-table-column(label="Precio vigente" width="130")
          template(slot-scope="{ row }")
            span(v-if="getCurrentPrice(row)") {{ formatCurrency(getCurrentPrice(row).amount) }}
            span(v-else) Sin precio
        el-table-column(label="Copago" width="110")
          template(slot-scope="{ row }")
            span(v-if="getCurrentPrice(row) && getCurrentPrice(row).hasCopago")
              | {{ formatCurrency(getCurrentPrice(row).copagoAmount) }}
            span(v-else) -
        el-table-column(label="Coseguro" width="110")
          template(slot-scope="{ row }")
            span(v-if="getCurrentPrice(row) && getCurrentPrice(row).hasCoseguro")
              | {{ getCurrentPrice(row).coseguroPercent }}%
            span(v-else) -
        el-table-column(label="Habilitada" width="100")
          template(slot-scope="{ row }")
            el-switch(:value="row.isEnabled" @change="toggleItemEnabled(row)")
        el-table-column(label="" width="120")
          template(slot-scope="{ row }")
            el-button(size="mini" icon="el-icon-price-tag" @click="openPriceDialog(row)") Precio
            el-button(size="mini" type="danger" icon="el-icon-delete" @click="removeItem(row)")

      .pagination-wrapper(style="margin-top: 16px; text-align: center")
        el-pagination(
          v-if="itemsTotal > itemsPageSize"
          layout="prev, pager, next, total"
          :total="itemsTotal"
          :page-size="itemsPageSize"
          :current-page="itemsPage"
          @current-change="p => { itemsPage = p; fetchItems() }"
        )

  //- Dialog agregar prestación
  el-dialog(title="Agregar prestación" :visible.sync="itemDialogVisible" width="560px")
    el-form(label-position="top" size="small")
      el-form-item(label="Prestación" required :error="itemFormErrors.practice")
        el-select(
          v-model="itemForm.practice"
          filterable
          remote
          reserve-keyword
          placeholder="Buscar por código o nombre..."
          :remote-method="searchPractices"
          :loading="practiceSearchLoading"
          style="width: 100%"
        )
          el-option(v-for="o in practiceOptions" :key="o.value" :label="o.label" :value="o.value")
      el-form-item(label=" ")
        el-checkbox(v-model="itemForm.isEnabled") Prestador la brinda
      .form-card
        .form-card__title Precio inicial
        el-row(:gutter="12")
          el-col(:span="12")
            el-form-item(label="Precio base $")
              el-input-number(v-model="itemForm.amount" :min="0" :precision="2" style="width: 100%")
          el-col(:span="12")
            el-form-item(label="Vigente desde")
              el-input(v-model="itemForm.effectiveFrom" type="date" style="width: 100%")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="itemForm.hasCopago") Copago
          el-col(:span="8")
            el-form-item(label="Monto copago $")
              el-input-number(v-model="itemForm.copagoAmount" :min="0" :precision="2" style="width: 100%" :disabled="!itemForm.hasCopago")
        el-row(:gutter="12")
          el-col(:span="8")
            el-form-item(label=" ")
              el-checkbox(v-model="itemForm.hasCoseguro") Coseguro
          el-col(:span="8")
            el-form-item(label="% Coseguro")
              el-input-number(v-model="itemForm.coseguroPercent" :min="0" :max="100" style="width: 100%" :disabled="!itemForm.hasCoseguro")
    span(slot="footer")
      el-button(@click="itemDialogVisible = false") Cancelar
      el-button(type="primary" :loading="itemSaving" @click="saveItem") Agregar

  //- Dialog nuevo precio
  el-dialog(title="Actualizar precio" :visible.sync="priceDialogVisible" width="480px")
    p(v-if="selectedItem" style="color: #666; margin-bottom: 16px")
      | {{ selectedItem.practice ? selectedItem.practice.code + ' - ' + selectedItem.practice.name : '' }}
    el-form(label-position="top" size="small")
      el-row(:gutter="12")
        el-col(:span="12")
          el-form-item(label="Precio base $")
            el-input-number(v-model="priceForm.amount" :min="0" :precision="2" style="width: 100%")
        el-col(:span="12")
          el-form-item(label="Vigente desde")
            el-input(v-model="priceForm.effectiveFrom" type="date" style="width: 100%")
      el-row(:gutter="12")
        el-col(:span="8")
          el-form-item(label=" ")
            el-checkbox(v-model="priceForm.hasCopago") Copago
        el-col(:span="8")
          el-form-item(label="Monto $")
            el-input-number(v-model="priceForm.copagoAmount" :min="0" :precision="2" style="width: 100%" :disabled="!priceForm.hasCopago")
      el-row(:gutter="12")
        el-col(:span="8")
          el-form-item(label=" ")
            el-checkbox(v-model="priceForm.hasCoseguro") Coseguro
        el-col(:span="8")
          el-form-item(label="% Coseguro")
            el-input-number(v-model="priceForm.coseguroPercent" :min="0" :max="100" style="width: 100%" :disabled="!priceForm.hasCoseguro")
    span(slot="footer")
      el-button(@click="priceDialogVisible = false") Cancelar
      el-button(type="primary" :loading="priceSaving" @click="savePrice") Guardar precio
</template>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}
.items-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.form-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.form-card__title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #303133;
}
</style>
